import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { Clock, Eye, Hand, MessageSquareOff, OctagonPause, Pause, Pencil, Play, ShieldAlert, Square } from 'lucide-react'
import type { MessageView, SwitchScope, SwitchesView } from '@takeit4me/shared'
import { api } from '../api'
import { useMe } from '../auth'
import { useAction, useNow, useQuery, type QueryState } from '../lib/hooks'
import { when } from '../lib/format'
import { countdown, isWaitingMessage, pauseHeadline, pausedItems, pendingState, SCOPE_LABELS, type PauseItem } from '../lib/sending'
import { ChannelBadge } from './ChannelBadge'
import { useOnLiveEvent } from './Layout'
import { Pill } from './Pill'
import { Field, Modal, useToast } from './ui'

/**
 * Sending controls (design 2.2, 13.1): kill switches and messages waiting to go. A pause holds every outbound
 * message in place; lifting it sends them. Platform and region pauses come from takeit4me and are read-only.
 */

// ---------------------------------------------------------------- switches state, shared by the shell and pages
const SwitchesCtx = createContext<QueryState<SwitchesView> | null>(null)
export function SwitchesProvider({ q, children }: { q: QueryState<SwitchesView>; children: ReactNode }) {
  return <SwitchesCtx.Provider value={q}>{children}</SwitchesCtx.Provider>
}
export function useSwitches() {
  const q = useContext(SwitchesCtx)
  return { data: q?.data ?? null, error: q?.error ?? null, reload: () => (q ? q.reload(true) : Promise.resolve()) }
}
/** Admins and supervisors can pause and resume (PUT /switches). */
export function useCanPause() {
  const me = useMe()
  return me.staff.role === 'admin' || me.staff.role === 'supervisor'
}

function useResume() {
  const toast = useToast()
  const sw = useSwitches()
  return useAction(async (scope: SwitchScope, holdAi: boolean) => {
    await api.setSwitch({ scope, paused: false, hold_ai: holdAi })
    await sw.reload()
    const what = scope === 'company' ? 'Sending resumed.' : `${SCOPE_LABELS[scope]} sending resumed.`
    toast(holdAi ? `${what} Approved and staff messages go now; the AI's replies written during the pause are on the desk for review.` : `${what} Waiting messages go now.`)
  }, { onError: (e) => toast(e.message, 'error') })
}

/**
 * Resume, with the safer choice as the default: the AI's own replies written while sending was paused go to the
 * desk for a person (the pause may have been because the AI was answering badly), rather than out on their own.
 */
function ResumeButton({ scope }: { scope: SwitchScope }) {
  const resume = useResume()
  const [open, setOpen] = useState(false)
  const [holdAi, setHoldAi] = useState(true)
  return (
    <>
      <button className="btn sm" disabled={resume.busy} onClick={() => { setHoldAi(true); setOpen(true) }}><Play /> Resume</button>
      <Modal open={open} onClose={() => setOpen(false)} title={`Resume ${scope === 'company' ? 'all sending' : `${SCOPE_LABELS[scope]} sending`}?`}
        footer={<><button className="btn" onClick={() => setOpen(false)}>Cancel</button><button className="btn pri" disabled={resume.busy} onClick={async () => { await resume.run(scope, holdAi); setOpen(false) }}><Play /> Resume</button></>}>
        <div className="text-[12.5px] text-muted mb-2">Messages people approved or wrote go out straight away.</div>
        <label className="flex items-start gap-2 text-[13px] cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={holdAi} onChange={(e) => setHoldAi(e.target.checked)} />
          <span><b>Hold the AI's replies written during the pause for review</b> (recommended). They go to the desk instead of out on their own. Untick to send them now.</span>
        </label>
      </Modal>
    </>
  )
}

function byLine(i: PauseItem) {
  if (i.readOnly) return i.reason ? `Reason: ${i.reason}` : 'takeit4me will lift it when it is safe.'
  const who = [i.set_by && `by ${i.set_by}`, i.set_at && when(i.set_at)].filter(Boolean).join(', ')
  return [i.reason ? `Reason: ${i.reason}` : 'No reason given', who && `Paused ${who}`].filter(Boolean).join(' · ')
}

/** Every paused scope with its reason, who set it and (for admins and supervisors) Resume. */
export function PausedList({ items, compact = false }: { items: PauseItem[]; compact?: boolean }) {
  const canPause = useCanPause()
  return (
    <div className="flex flex-col gap-1.5">
      {items.map((i) => (
        <div key={i.scope} className={`flex items-start gap-2 ${compact ? 'text-[12.5px]' : 'text-[13px]'}`}>
          {i.readOnly ? <ShieldAlert size={16} className="text-red-600 shrink-0 mt-0.5" /> : <Pause size={16} className="text-red-600 shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <div className="font-semibold">{i.title}</div>
            <div className="text-[12px] opacity-80">{byLine(i)}</div>
          </div>
          {!i.readOnly && canPause && <ResumeButton scope={i.scope as SwitchScope} />}
          {i.readOnly && <span className="text-[11px] opacity-70 whitespace-nowrap mt-0.5">Paused by takeit4me</span>}
        </div>
      ))}
    </div>
  )
}

/** The compact indicator in the top bar on every page; opens the details. */
export function PauseIndicator() {
  const sw = useSwitches()
  const [open, setOpen] = useState(false)
  const items = pausedItems(sw.data)
  const head = pauseHeadline(items)
  if (!head) return null
  return (
    <>
      <button onClick={() => setOpen(true)} title={items.map((i) => `${i.title}${i.reason ? `: ${i.reason}` : ''}`).join('\n')}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 text-red-700 font-semibold text-[12px] px-2.5 h-[30px]">
        <OctagonPause size={14} /> {head}
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Sending is paused">
        <div className="text-muted text-[12.5px] mb-3">Nothing is lost: messages wait and go out as soon as the pause is lifted. Incoming messages are still received and answered as drafts.</div>
        <PausedList items={items} />
      </Modal>
    </>
  )
}

/** The banner on Today whenever anything is paused. */
export function PausedBanner() {
  const sw = useSwitches()
  const items = pausedItems(sw.data)
  if (!items.length) return null
  return (
    <div className="mx-5 mt-4 rounded-xl border-[1.5px] border-red-200 bg-red-50 text-red-900 px-4 py-3">
      <div className="flex items-center gap-2 font-bold text-[14px] mb-1.5"><OctagonPause size={18} className="text-red-600" /> Sending is paused</div>
      <div className="text-[12.5px] text-red-900/80 mb-2">Replies, journeys and notifications wait in place and go out when the pause is lifted. Nothing is lost.</div>
      <PausedList items={items} />
    </div>
  )
}

/** "Pause sending": the whole company or one channel, with a required reason. */
export function PauseSendingModal({ open, onClose, scope: fixed }: { open: boolean; onClose: () => void; scope?: SwitchScope }) {
  const toast = useToast()
  const sw = useSwitches()
  const [scope, setScope] = useState<SwitchScope>(fixed ?? 'company')
  const [reason, setReason] = useState('')
  useEffect(() => { if (open) { setScope(fixed ?? 'company'); setReason('') } }, [open, fixed])
  const already = scope === 'company' ? sw.data?.company.paused : sw.data?.channels?.[scope]
  const pause = useAction(async () => {
    await api.setSwitch({ scope, paused: true, reason: reason.trim() })
    await sw.reload()
    toast(scope === 'company' ? 'Sending is paused for the company' : `${SCOPE_LABELS[scope]} sending is paused`)
    onClose()
  }, { onError: (e) => toast(e.message, 'error') })
  return (
    <Modal open={open} onClose={onClose} title="Pause sending"
      footer={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn red" disabled={!reason.trim() || pause.busy || !!already} onClick={() => pause.run()}><Pause /> Pause sending</button>
      </>}>
      <div className="flex flex-col gap-3">
        <div className="text-[12.5px] text-muted">Outbound messages wait until you resume; nothing is lost or sent twice. Customers can still write in, and the AI still drafts replies.</div>
        {!fixed ? (
          <Field label="What to pause">
            <select className="select w-full" value={scope} onChange={(e) => setScope(e.target.value as SwitchScope)}>
              {(Object.keys(SCOPE_LABELS) as SwitchScope[]).map((k) => <option key={k} value={k}>{SCOPE_LABELS[k]}</option>)}
            </select>
          </Field>
        ) : <div className="text-[13px]"><b>{SCOPE_LABELS[fixed]}</b> sending for the company</div>}
        <Field label="Why?" hint="Shown to everyone in the console until it is lifted.">
          <textarea className="textarea w-full" rows={3} autoFocus maxLength={300} placeholder="e.g. The SMS provider is sending duplicates; checking with them." value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {already && <div className="text-[12px] text-red-700">This is already paused.</div>}
      </div>
    </Modal>
  )
}

/** The Pause sending button (admins and supervisors only). */
export function PauseSendingButton({ scope, className = 'btn sm' }: { scope?: SwitchScope; className?: string }) {
  const can = useCanPause()
  const [open, setOpen] = useState(false)
  if (!can) return null
  return (
    <>
      <button className={className} onClick={() => setOpen(true)}><Pause /> Pause sending</button>
      <PauseSendingModal open={open} onClose={() => setOpen(false)} scope={scope} />
    </>
  )
}

// ---------------------------------------------------------------- pending sends
export type StopMode = 'hold' | 'no_reply_needed' | 'discard'
const STOP_WORDS: Record<StopMode, { placeholder: string; button: string; done: string }> = {
  hold: { placeholder: 'Why hold it? (shown on the desk; the AI learns from this)', button: 'Hold for review', done: 'Held for review. It is on the desk with the draft kept.' },
  no_reply_needed: { placeholder: 'Why does it need no reply?', button: 'Close, no reply', done: 'Closed. Nothing was sent.' },
  discard: { placeholder: 'Why stop it?', button: 'Stop', done: 'Stopped. It will not be sent.' },
}

/**
 * Stop with a reason, inline (never a browser prompt). An AI reply is held for review by default: it goes to the
 * desk with its draft kept (a veto). "No reply needed" closes the conversation instead.
 */
export function StopForm({ messageId, mode = 'discard', onStopped, onCancel }: { messageId: string; mode?: StopMode; onStopped: (r?: { exception_id: string | null }) => void; onCancel: () => void }) {
  const toast = useToast()
  const [reason, setReason] = useState('')
  const w = STOP_WORDS[mode]
  const stop = useAction(async (why: string) => {
    const r = await api.cancelMessage(messageId, why, mode)
    toast(w.done)
    onStopped(r)
  }, { onError: (e) => { toast(e.message, 'error'); onStopped() } })
  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      <form className="flex items-center gap-1.5" onSubmit={(e) => { e.preventDefault(); if (reason.trim()) void stop.run(reason.trim()) }}>
        <input className="input h-7 flex-1 min-w-0 text-[12px]" autoFocus placeholder={w.placeholder} value={reason} onChange={(e) => setReason(e.target.value)} />
        <button className={`btn sm ${mode === 'hold' ? 'pri' : 'red'}`} disabled={!reason.trim() || stop.busy}>{mode === 'hold' ? <Hand /> : <Square />} {w.button}</button>
        <button type="button" className="btn sm" onClick={onCancel}>Keep</button>
      </form>
    </div>
  )
}

/** On a timeline message: "Sending in 04:12 · Hold for review", or "Waiting: sending is paused · Stop". */
export function PendingSendControl({ m, onChanged }: { m: MessageView; onChanged?: () => void }) {
  const me = useMe()
  const nav = useNavigate()
  const now = useNow(1000)
  const [stopping, setStopping] = useState<StopMode | null>(null)
  if (!isWaitingMessage(m)) return null
  const canStop = me.spaces.includes('exceptions')
  const paused = m.policy?.paused
  const left = countdown(m.send_after, now)
  const ai = m.author_kind === 'ai'
  return (
    <div className="mt-1 rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-2.5 py-1.5 text-[12px]">
      <div className="flex items-center gap-1.5 flex-wrap">
        {paused ? <Pause size={13} /> : <Clock size={13} />}
        <span className="flex-1">{paused ? `Waiting: ${paused}` : left === 'now' ? 'Sending now' : <>Sending in <b className="tabular-nums">{left}</b> unless someone stops it</>}</span>
        {canStop && !stopping && (ai
          ? <>
              <button className="font-semibold" onClick={() => setStopping('hold')}>Hold for review</button>
              <button className="font-semibold" onClick={() => setStopping('no_reply_needed')}>No reply needed</button>
            </>
          : <button className="text-red-700 font-semibold" onClick={() => setStopping('discard')}>Stop</button>)}
      </div>
      {stopping && <StopForm messageId={m.id} mode={stopping} onStopped={(r) => { setStopping(null); onChanged?.(); if (r?.exception_id) nav(`/exceptions/${r.exception_id}`) }} onCancel={() => setStopping(null)} />}
    </div>
  )
}

/**
 * Replies waiting to send (pending auto-sends in the review queue): AI replies in their veto window and
 * anything held by a pause, each with Hold for review, Edit and No reply needed (AI replies) or Stop. Used on Today
 * (the first few) and as the desk's "Sending soon" tab (all of them).
 */
export function PendingSendsList({ limit, empty }: { limit?: number; empty?: string }) {
  const now = useNow(1000)
  const nav = useNavigate()
  const toast = useToast()
  const me = useMe()
  const q = useQuery(() => api.pendingMessages(), [], { refreshMs: 30_000 })
  const [stopping, setStopping] = useState<{ id: string; mode: StopMode } | null>(null)
  useOnLiveEvent((e) => e.type === 'message' || e.type === 'switches' || e.type === 'exception.updated', () => void q.reload(true))
  const items = q.data?.items ?? []
  const shown = limit ? items.slice(0, limit) : items
  // Once a countdown runs out the send job takes it: refresh shortly after so it leaves the list.
  const due = items.filter((p) => pendingState(p, now) === 'due').length
  useEffect(() => {
    if (!due) return
    const t = setTimeout(() => void q.reload(true), 4000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [due])
  const canAct = me.spaces.includes('exceptions')
  // Edit: hold it for review, then open it on the desk with the editor open.
  const edit = useAction(async (id: string) => {
    const r = await api.cancelMessage(id, `Editing it before it goes (${me.staff.name})`, 'hold')
    if (r.exception_id) nav(`/exceptions/${r.exception_id}?edit=1`)
    else toast('Held for review.')
  }, { onError: (e) => toast(e.message, 'error') })
  if (q.error && !q.data) return <div className="text-red-700 text-[12.5px] py-2">Could not load the replies waiting to send: {q.error.message} <button className="btn sm" onClick={() => void q.reload()}>Try again</button></div>
  if (!q.data) return <div className="text-muted text-[12.5px] py-2">Loading…</div>
  if (!items.length) return <div className="text-muted text-[12.5px] py-2">{empty ?? 'Nothing waiting. Replies in a veto window, and anything held by a pause, show here so you can stop them.'}</div>
  return (
    <>
      {shown.map((p) => {
        const state = pendingState(p, now)
        const ai = p.author_kind === 'ai'
        return (
          <div key={p.id} className="py-2 border-t border-line2 first:border-t-0 text-[12.5px]">
            <div className="flex items-center gap-2 flex-wrap">
              <ChannelBadge channel={p.channel} size={20} />
              <b className="truncate">{p.person_name ?? 'Unknown customer'}</b>
              {p.conversation_ref && <span className="text-faint text-[11px]">{p.conversation_ref}</span>}
              <span className="ml-auto flex items-center gap-1.5 shrink-0 flex-wrap">
                {state === 'paused' ? <Pill tone="red" title={p.paused ?? undefined}><Pause /> Paused</Pill>
                  : state === 'veto' ? <Pill tone="blue"><Clock /> <span className="tabular-nums">in {countdown(p.send_after, now)}</span></Pill>
                    : <Pill tone="gray">Sending now</Pill>}
                {p.held_up_by && <Pill tone="amber" title="It waits while someone has the conversation open (at most 30 minutes, then it goes to the desk)"><Eye /> {p.held_up_by} has it open</Pill>}
                {canAct && stopping?.id !== p.id && state !== 'due' && (ai && p.conversation_id ? (
                  <>
                    <button className="btn sm" disabled={edit.busy} onClick={() => void edit.run(p.id)}><Pencil /> Edit</button>
                    <button className="btn sm" onClick={() => setStopping({ id: p.id, mode: 'hold' })}><Hand /> Hold for review</button>
                    <button className="btn sm" onClick={() => setStopping({ id: p.id, mode: 'no_reply_needed' })}><MessageSquareOff /> No reply needed</button>
                  </>
                ) : <button className="btn sm" onClick={() => setStopping({ id: p.id, mode: 'discard' })}><Square /> Stop</button>)}
              </span>
            </div>
            <div className="text-muted mt-0.5 line-clamp-2">{ai ? 'AI: ' : ''}{p.preview}</div>
            {state === 'paused' && p.paused && <div className="text-[11px] text-red-700 mt-0.5">{p.paused}</div>}
            {stopping?.id === p.id && <StopForm messageId={p.id} mode={stopping.mode} onStopped={(r) => { setStopping(null); void q.reload(true); if (r?.exception_id) nav(`/exceptions/${r.exception_id}`) }} onCancel={() => setStopping(null)} />}
          </div>
        )
      })}
      {limit && items.length > limit && <div className="text-muted text-[12px] pt-2">and {items.length - limit} more waiting · <Link className="text-brand font-semibold" to="/exceptions?tab=sending">See them all</Link></div>}
    </>
  )
}

/** How many replies are waiting to send (the desk's "Sending soon" tab count). */
export function usePendingCount() {
  const q = useQuery(() => api.pendingMessages(), [], { refreshMs: 30_000 })
  useOnLiveEvent((e) => e.type === 'message' || e.type === 'switches', () => void q.reload(true))
  return q.data?.items.length ?? null
}

/** Fetches the switches for the shell; refreshed by Layout on 'switches' live events. */
export function useSwitchesQuery() {
  return useQuery(() => api.switches(), [], { refreshMs: 60_000 })
}
